import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CalendarHeart, ListChecks } from "@phosphor-icons/react";

import { Task } from "api/types/users";
import { GET_USER_TASKS } from "@constants/reactQueryKeys";
import { useGetUserTask } from "hooks/api/user/useGetUserTask";
import useUpdateQueryData from "hooks/useUpdateQueryCache";
import { findDifferenceInMinutes } from "utils/findDifferenceInMinutes";
import { UserTaskType } from "./Interfaces";
import FilloutTask from "./FilloutTask";
import CallSignatureTask from "./CallSignatureTask";
import ImageUploadTask from "./ImageUploadTask";
import TaskChecklist from "./MyTasks/TaskChecklist";
import DashboardHeader from "./NextAppointment/DashboardHeader";
import NextAppointment from "./NextAppointment";
import TechCheckTask from "./TechCheckTask";
import TreatmentPlanSignature from "./forms/TreatmentPlanSignature";
import DocumentSignature from "./forms/DocumentSignature";

const Dashboard = (props: any) => {
  const { classes, user, videoCalls, treatmentPlan, onSubmitTreatmentPlanApproval, clientId } =
    props;

  const { t } = useTranslation(["dashboard"]);

  // Hook to fetch user tasks
  const { tasks, refetchTasks } = useGetUserTask(user?.id, { client_id: clientId });
  // Hook to update the React Query cache
  const { setQueryData } = useUpdateQueryData<Task[]>([GET_USER_TASKS, user?.id, clientId]);

  // State
  const [currentTask, setCurrentTask] = useState<Task | null>(null);

  const callList =
    videoCalls?.length > 0
      ? videoCalls.filter(
          (call: any) =>
            !call.end_date &&
            findDifferenceInMinutes(new Date(), call.scheduled_date) < call.call_duration
        )
      : [];

  // Function to optimistically update the task list in the React Query cache
  const optimisticUpdateTask = async (taskId: number) => {
    // Refetch the tasks to get the latest data
    await refetchTasks();

    setQueryData((currentData) => {
      if (!currentData || !Array.isArray(currentData?.data)) {
        return currentData as any;
      }

      // return the updated task
      return {
        ...currentData,
        data: currentData.data.map((task) =>
          task.id === taskId ? { ...task, completed: true } : task
        ),
      };
    });
  };

  const handleTaskToggle = (task: Task) => {
    setCurrentTask(task);
  };

  // When the client returns from the tech check flow (svc-video redirects them
  // back with ?from=tech_check&status=complete), refetch the task list so the
  // freshly-completed task shows as done immediately.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") === "tech_check" && params.get("status") === "complete") {
      refetchTasks();
      params.delete("from");
      params.delete("status");
      const nextSearch = params.toString();
      const url =
        window.location.pathname + (nextSearch ? `?${nextSearch}` : "") + window.location.hash;
      window.history.replaceState({}, "", url);
    }
    // The query params are consumed and cleared inline, so even if
    // `refetchTasks`'s identity changes this never re-fires for the same
    // navigation (the guard above fails once the params are gone).
  }, [refetchTasks]);

  // List of task types and their corresponding components
  const TaskComponentMap: Record<UserTaskType, (data: Task) => React.ReactNode> = useMemo(
    () => ({
      [UserTaskType.FILLOUT_IFRAME]: (data: Task) => (
        <FilloutTask data={data} setCurrentTask={setCurrentTask} onSubmit={optimisticUpdateTask} />
      ),
      [UserTaskType.IMAGE_UPLOAD]: (data: Task) => (
        <ImageUploadTask
          data={data}
          setCurrentTask={setCurrentTask}
          onUploadSuccess={refetchTasks}
        />
      ),
      [UserTaskType.VIDEO_CALL_SIGNATURE]: (data: Task) => (
        <CallSignatureTask data={data} setCurrentTask={setCurrentTask} onSubmit={refetchTasks} />
      ),
      [UserTaskType.TREATMENT_PLAN_SIGNATURE]: (data: Task) => (
        <TreatmentPlanSignature
          open={true}
          onClose={() => setCurrentTask(null)}
          treatmentPlan={treatmentPlan}
          onSubmitTreatmentPlanApproval={onSubmitTreatmentPlanApproval}
          optimisticUpdateTask={optimisticUpdateTask}
          data={data}
        />
      ),
      [UserTaskType.NINETY_DAY_PROGRESS_SIGNATURE]: (data: Task) => (
        <DocumentSignature
          open={true}
          onClose={() => setCurrentTask(null)}
          refetchTasks={refetchTasks}
          data={data}
        />
      ),
      [UserTaskType.OTHER_CONSENT_FORMS]: () => null,
      [UserTaskType.TECH_CHECK]: (data: Task) => (
        <TechCheckTask data={data} clientId={clientId} setCurrentTask={setCurrentTask} />
      ),
    }),
    [
      setCurrentTask,
      refetchTasks,
      treatmentPlan,
      onSubmitTreatmentPlanApproval,
      optimisticUpdateTask,
      clientId,
    ]
  );

  return (
    <>
      <div className={classes.responsiveDashboardContainer}>
        <div className={classes.dashboardSection}>
          <DashboardHeader Icon={CalendarHeart} title={t("dashboard:parent.nextAppointment.title")} />
          <NextAppointment calls={callList} />
        </div>

        <div className={classes.dashboardSection}>
          <DashboardHeader Icon={ListChecks} title={t("dashboard:parent.myTasks.title")} />
          <TaskChecklist tasks={tasks || []} onTaskToggle={handleTaskToggle} clientId={clientId} />
        </div>
      </div>

      {currentTask && TaskComponentMap[currentTask.type](currentTask)}
    </>
  );
};

export default Dashboard;
